// Persistent Global Bluetooth Service for Prabhu Labels
let activeDevice = null;
let activeChar = null;
let isConnecting = false;

const SERVICE_UUIDS = [
  "000018f0-0000-1000-8000-00805f9b34fb",
  "e7810a71-73ae-499d-8c15-faa9aef0c3f2",
  "49535343-fe7d-4ae5-8fa9-9fafd205e455",
  "0000ff00-0000-1000-8000-00805f9b34fb",
  "0000fee7-0000-1000-8000-00805f9b34fb",
  "000018f1-0000-1000-8000-00805f9b34fb",
  "0000ae00-0000-1000-8000-00805f9b34fb",
  "0000af00-0000-1000-8000-00805f9b34fb"
];

const connectWithTimeout = (device, ms = 3500) => {
  return Promise.race([
    device.gatt.connect(),
    new Promise((_, reject) => setTimeout(() => reject(new Error("Printer connection timed out. Make sure printer is ON.")), ms))
  ]);
};

const findWriteChar = async (server) => {
  for (const uuid of SERVICE_UUIDS) {
    try {
      const service = await server.getPrimaryService(uuid);
      if (service) {
        const chars = await service.getCharacteristics();
        for (const c of chars) {
          if (c.properties.write || c.properties.writeWithoutResponse) {
            return c;
          }
        }
      }
    } catch (e) {}
  }

  try {
    const services = await server.getPrimaryServices();
    for (const s of services) {
      try {
        const chars = await s.getCharacteristics();
        for (const c of chars) {
          if (c.properties.write || c.properties.writeWithoutResponse) {
            return c;
          }
        }
      } catch (err) {}
    }
  } catch (err) {}
  return null;
};

export const initBluetoothBackground = async () => {
  if (typeof window === "undefined" || !navigator.bluetooth) return null;
  if (activeDevice && activeDevice.gatt && activeDevice.gatt.connected && activeChar) {
    return { device: activeDevice, char: activeChar };
  }
  if (isConnecting) return null;

  isConnecting = true;
  try {
    if (navigator.bluetooth && navigator.bluetooth.getDevices) {
      const devices = await navigator.bluetooth.getDevices().catch(() => []);
      if (devices && devices.length > 0) {
        const dev = devices[0];
        activeDevice = dev;
        
        dev.addEventListener("gattserverdisconnected", () => {
          activeChar = null;
          setTimeout(initBluetoothBackground, 1500);
        });

        if (!dev.gatt.connected) {
          const server = await connectWithTimeout(dev, 3000);
          activeChar = await findWriteChar(server);
        } else {
          activeChar = await findWriteChar(dev.gatt);
        }

        window.__BT_PRINTER__ = { device: activeDevice, char: activeChar };
        return { device: activeDevice, char: activeChar };
      }
    }
  } catch (err) {
    // Bluetooth unavailable or disabled on system
  } finally {
    isConnecting = false;
  }
  return null;
};

export const getConnectedPrinter = async (forcePair = false) => {
  if (!navigator.bluetooth) {
    throw new Error("Web Bluetooth is not supported in this browser");
  }

  if (!forcePair && activeDevice && activeDevice.gatt && activeDevice.gatt.connected && activeChar) {
    return { device: activeDevice, char: activeChar };
  }

  if (!forcePair && window.__BT_PRINTER__?.char && window.__BT_PRINTER__?.device?.gatt?.connected) {
    activeDevice = window.__BT_PRINTER__.device;
    activeChar = window.__BT_PRINTER__.char;
    return { device: activeDevice, char: activeChar };
  }

  if (!forcePair && navigator.bluetooth.getDevices) {
    try {
      const devices = await navigator.bluetooth.getDevices();
      if (devices && devices.length > 0) {
        const dev = devices[0];
        activeDevice = dev;
        dev.addEventListener("gattserverdisconnected", () => {
          activeChar = null;
          setTimeout(initBluetoothBackground, 1500);
        });
        const server = await connectWithTimeout(dev, 3000);
        activeChar = await findWriteChar(server);
        if (activeChar) {
          window.__BT_PRINTER__ = { device: activeDevice, char: activeChar };
          return { device: activeDevice, char: activeChar };
        }
      }
    } catch (e) {
      console.log("[BT] Silent reconnect:", e?.message || e);
    }
  }

  const device = await navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: SERVICE_UUIDS
  });

  activeDevice = device;
  device.addEventListener("gattserverdisconnected", () => {
    activeChar = null;
    setTimeout(initBluetoothBackground, 1500);
  });

  const server = await connectWithTimeout(device, 4000);
  activeChar = await findWriteChar(server);
  if (!activeChar) throw new Error("No writable characteristic found on printer");

  window.__BT_PRINTER__ = { device: activeDevice, char: activeChar };
  return { device: activeDevice, char: activeChar };
};

export const sendEscPos = async (char, dataArray) => {
  const chunkSize = 80;
  const buffer = new Uint8Array(dataArray);
  for (let i = 0; i < buffer.length; i += chunkSize) {
    await char.writeValue(buffer.slice(i, i + chunkSize));
    await new Promise((r) => setTimeout(r, 20));
  }
};

export const autoConnectAndPrint = async (printData, onStatus) => {
  if (!navigator.bluetooth) {
    throw new Error("Web Bluetooth is not supported on this browser.");
  }

  onStatus?.("Connecting to printer...");
  let printer = null;

  try {
    printer = await getConnectedPrinter(false);
  } catch (err) {
    console.log("[BT] Auto-connect prompt:", err);
    onStatus?.("Please select printer to pair...");
    printer = await getConnectedPrinter(true);
  }

  if (!printer || !printer.char) {
    throw new Error("Could not connect to printer");
  }

  const { device, char } = printer;
  onStatus?.(`Printing to ${device.name || "Printer"}...`);

  await sendEscPos(char, printData);
  onStatus?.("✅ Printed Successfully!");
  return device;
};

export const buildChallanEscPos = (orderOrOrders, weight = 0, type = "challan", cartage = 0) => {
  const encoder = new TextEncoder();
  const init = new Uint8Array([0x1b, 0x40]);
  const center = new Uint8Array([0x1b, 0x61, 0x01]);
  const left = new Uint8Array([0x1b, 0x61, 0x00]);
  const boldOn = new Uint8Array([0x1b, 0x45, 0x01]);
  const boldOff = new Uint8Array([0x1b, 0x45, 0x00]);
  const doubleSize = new Uint8Array([0x1d, 0x21, 0x11]);
  const normalSize = new Uint8Array([0x1d, 0x21, 0x00]);
  const cut = new Uint8Array([0x1d, 0x56, 0x42, 0x00]);

  const ordersList = Array.isArray(orderOrOrders)
    ? orderOrOrders
    : orderOrOrders?.allOrders && Array.isArray(orderOrOrders.allOrders)
    ? orderOrOrders.allOrders
    : orderOrOrders?.orders && Array.isArray(orderOrOrders.orders)
    ? orderOrOrders.orders
    : [orderOrOrders];

  const firstOrder = ordersList[0];
  const dateStr = firstOrder?.updatedAt
    ? new Date(firstOrder.updatedAt).toLocaleDateString("en-IN")
    : new Date().toLocaleDateString("en-IN");

  const title = type === "cartage" ? "CARTAGE SLIP" : "DISPATCH CHALLAN";

  const isTagOrder = (o) => {
    const prodGroup = (o?.product?.group || "").toLowerCase().trim();
    const ordGroup = (o?.group || "").toLowerCase().trim();
    const prodName = (o?.product?.productName || o?.description || "").toLowerCase().trim();
    return prodGroup === "tag" || ordGroup === "tag" || prodName.includes("tag");
  };

  const totalTagQty = ordersList.filter(isTagOrder).reduce(
    (s, o) => s + (parseFloat(o?.quantity) || 0),
    0
  );
  const totalAllQty = ordersList.reduce(
    (s, o) => s + (parseFloat(o?.quantity) || 0),
    0
  );

  // Group by customer
  const customerMap = new Map();
  const customerGroups = [];
  ordersList.forEach((ord) => {
    const custName = ord?.customer?.userName || "Customer";
    const grp = ord?.group || ord?.customer?.group?.name || "";
    const key = `${custName}___${grp}`;
    if (!customerMap.has(key)) {
      const g = { customerName: custName, group: grp, orders: [] };
      customerMap.set(key, g);
      customerGroups.push(g);
    }
    customerMap.get(key).orders.push(ord);
  });

  const parsedWeight = parseFloat(weight);
  const hasInputWeight = !isNaN(parsedWeight) && parsedWeight > 0;
  let displayWeight = 0;
  if (hasInputWeight) {
    displayWeight = parsedWeight;
  } else {
    const sumW = ordersList.reduce(
      (sum, o) => sum + (parseFloat(o?.challanWeight || o?.weight || 0) || 0),
      0
    );
    if (sumW > 0) displayWeight = Number(sumW.toFixed(3));
  }

  const makeSingleCombinedSlip = () => {
    let customerBlocksBytes = [];
    customerGroups.forEach((cg) => {
      const headerTitle = `${cg.customerName} ${cg.group ? `(${cg.group})` : ""}\n`;
      customerBlocksBytes.push(...boldOn, ...encoder.encode(headerTitle), ...boldOff);

      cg.orders.forEach((ord) => {
        const name = ord?.product?.productName || ord?.description || "Product";
        const qty = String(ord?.quantity || 0);
        const spacesNeeded = Math.max(1, 32 - name.length - qty.length);
        const lineStr = `  ${name}${" ".repeat(Math.max(1, spacesNeeded - 2))}${qty}\n`;
        customerBlocksBytes.push(...encoder.encode(lineStr));
      });
      customerBlocksBytes.push(...encoder.encode("--------------------------------\n"));
    });

    return [
      ...center,
      ...doubleSize, ...boldOn,
      ...encoder.encode("PRABHU LABELS\n"),
      ...normalSize, ...boldOff,
      ...encoder.encode(`[ ${title} ]\n`),
      ...encoder.encode(`Date: ${dateStr}\n`),
      ...encoder.encode("================================\n"),
      ...left,
      ...customerBlocksBytes,
      ...(displayWeight && displayWeight > 0
        ? [
            ...center,
            ...boldOn,
            ...encoder.encode(`Weight: ${displayWeight} kg\n`),
            ...boldOff
          ]
        : []),
      ...(type === "cartage" && cartage && cartage !== "0" && cartage !== 0
        ? [
            ...center,
            ...boldOn,
            ...encoder.encode(`Cartage: Rs. ${cartage}\n`),
            ...boldOff
          ]
        : []),
      ...center,
      ...encoder.encode("Call for query: 9417771111\n"),
      ...encoder.encode("--------------------------------\n"),
      ...boldOn,
      ...encoder.encode("                 Receiver's Sign\n\n")
    ];
  };

  return [
    ...init,
    ...makeSingleCombinedSlip(),
    ...center,
    ...encoder.encode("- - - - - CUT HERE - - - - -\n\n"),
    ...makeSingleCombinedSlip(),
    ...encoder.encode("\n\n\n"),
    ...cut
  ];
};
